import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useLocation, useNavigate } from "react-router-dom";
import {
  uploadKnowledgeBaseDomainSpecificFile,
  uploadKnowledgeBaseArchetypesFile,
  uploadKnowledgeBaseClientSpecificFile,
} from "../../../services/webServices";
import LoadingModal from "../../common-components/LoadingModal";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";

const KnowledgeBaseUpload = () => {
  const fileInputRef = useRef();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [loadingModal, setLoadingModal] = useState({
    open: false,
    message: "",
    progress: null,
  });
  const [domainScore, setDomainScore] = useState(null);
  const [domainMessage, setDomainMessage] = useState("");
  const [allScores, setAllScores] = useState(null);
  const [statusBarActive, setStatusBarActive] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewDetection, setPreviewDetection] = useState(null);
  const [previewResults, setPreviewResults] = useState([]);

  const location = useLocation();
  const collection = location.state?.collection || "";
  const navigate = useNavigate();

  console.log("collection", collection);

  const DOMAIN_COLLECTIONS = [
    "Banking",
    "Insurance",
    "Healthcare",
    "eCommerce",
    "Cruise",
  ];

  const ARCHETYPE_COLLECTIONS = [
    "Monolithic",
    "LLM-Centric",
    "Cloud-Native",
    "SaaS",
    "Legacy",
    "Layered_Architecture",
    "MicroServices",
    "EventDrivenArchitecture",
  ];

  const CLIENT_COLLECTIONS = [
    "Thermo_Fisher",
    "SageSure",
    "Dollar_Tree",
    "NVIDIA",
    "Visual_Matrix",
    "QEStrategy",
    "AAA",
    "SecurityTesting"
  ];

  const isSupportedCollection = () => {
    return (
      DOMAIN_COLLECTIONS.includes(collection) ||
      ARCHETYPE_COLLECTIONS.includes(collection) ||
      CLIENT_COLLECTIONS.includes(collection)
    );
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validFiles = files.filter((file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      return ["pdf", "txt", "docx", "doc"].includes(ext);
    });

    if (validFiles.length !== files.length) {
      setUploadResult("Some files were skipped (unsupported format)");
    }

    setSelectedFiles(validFiles);
    setUploadResult(null);
    setDomainScore(null);
    setDomainMessage("");
  };

  const handleAreaClick = () => {
    fileInputRef.current.click();
  };

  const handleContinue = async () => {
    if (selectedFiles.length === 0) return;

    if (!isSupportedCollection()) {
      setUploadResult(
        "This collection is not yet configured for upload. Please use a different collection."
      );
      return;
    }

    setUploading(true);
    setUploadResult(null);
    setDomainScore(null);
    setDomainMessage("");
    setLoadingModal({
      open: true,
      message: "Uploading and analyzing file...",
      progress: null,
    });

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append("files", file));
    formData.append("collection", collection);

    try {
      // Use appropriate API based on collection
      let preview;
      if (ARCHETYPE_COLLECTIONS.includes(collection)) {
        preview = await uploadKnowledgeBaseArchetypesFile(formData, false);
      } else if (CLIENT_COLLECTIONS.includes(collection)) {
        preview = await uploadKnowledgeBaseClientSpecificFile(formData, false);
      } else {
        preview = await uploadKnowledgeBaseDomainSpecificFile(formData, false);
      }

      const results = preview?.data?.results || [];

      if (results.length > 0) {
        setPreviewResults(results);

        const firstDet = results[0].detection;
        setDomainScore(firstDet?.score || null);
        setDomainMessage(firstDet?.message || "");
      }

      setLoadingModal({ open: false, message: "", progress: null });
      setConfirmOpen(true);

      setLoadingModal({ open: false, message: "", progress: null });
      setConfirmOpen(true);
    } catch (error) {
      setUploadResult("Upload failed");
      setLoadingModal({ open: false, message: "", progress: null });
      if (error?.response?.data?.detection) {
        const det = error.response.data.detection;
        setPreviewDetection(det);
        setDomainMessage(det.message);
        setAllScores(det.all_scores || null);
        const selectedDomain = collection.replace(/_/g, " ").toLowerCase();
        let score = null;
        if (det.all_scores) {
          score =
            det.all_scores[collection] ??
            det.all_scores[selectedDomain] ??
            det.all_scores[
              selectedDomain.charAt(0).toUpperCase() + selectedDomain.slice(1)
            ] ??
            null;
        }
        setDomainScore(score !== null ? score : det.score);
        setConfirmOpen(true);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmUpload = async () => {
    setConfirmOpen(false);
    if (selectedFiles.length === 0) return;

    if (!isSupportedCollection()) {
      setUploadResult("This collection is not yet configured for upload.");
      return;
    }

    setUploading(true);
    setLoadingModal({
      open: true,
      message: "Uploading to database...",
      progress: null,
    });

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append("files", file));
    formData.append("collection", collection);

    try {
      let resp;
      if (ARCHETYPE_COLLECTIONS.includes(collection)) {
        resp = await uploadKnowledgeBaseArchetypesFile(formData, true);
      } else if (CLIENT_COLLECTIONS.includes(collection)) {
        resp = await uploadKnowledgeBaseClientSpecificFile(formData, false);
      } else {
        resp = await uploadKnowledgeBaseDomainSpecificFile(formData, true);
      }

      setUploadResult("Upload successful!");
      setLoadingModal({ open: false, message: "", progress: null });
    } catch (e) {
      setUploadResult("Upload failed");
      setLoadingModal({ open: false, message: "", progress: null });
    } finally {
      setUploading(false);
    }
  };

  const handleCancelUpload = () => {
    setConfirmOpen(false);
    navigate("/dashboard/admin-navigation/knowledgebase");
  };

  React.useEffect(() => {
    if (
      uploadResult === "Upload successful!" ||
      (uploadResult && uploadResult !== "Upload successful!")
    ) {
      setStatusBarActive(true);
    }
  }, [uploadResult]);

  return (
    <>
      <LoadingModal
        open={loadingModal.open}
        message={loadingModal.message}
        progress={loadingModal.progress}
      />

      {uploadResult === "Upload successful!" && (
        <Box
          sx={{
            width: "100%",
            maxWidth: 480,
            margin: "8px auto 0 auto",
            background: "#F7F7F5",
            borderRadius: "8px",
            border: "1px solid #e0e0e0",
            height: "50px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <span style={{ color: "#4CAF50", fontSize: 22 }}>✔</span>
            <Typography sx={{ color: "#222", fontWeight: 500 }}>
              Document has been uploaded Successfully
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setStatusBarActive(false);
              navigate("/dashboard/admin-navigation/knowledgebase");
            }}
            size="small"
            sx={{ color: "#222" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {uploadResult && uploadResult !== "Upload successful!" && (
        <Box
          sx={{
            width: "100%",
            maxWidth: 480,
            margin: "8px auto 0 auto",
            background: "#FFF5F5",
            borderRadius: "8px",
            border: "1px solid #f44336",
            height: "50px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ color: "#f44336", fontWeight: 500 }}>
              {uploadResult.includes("not yet configured")
                ? uploadResult
                : "PDF rejected due to low domain match or insufficient relevance."}
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setStatusBarActive(false);
              navigate("/dashboard/admin-navigation/knowledgebase");
            }}
            size="small"
            sx={{ color: "#f44336" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
          background: "#F7F7F5",
        }}
      >
        <Card
          sx={{
            minWidth: 500,
            maxWidth: 700,
            width: "100%",
            borderRadius: 4,
            boxShadow: 2,
          }}
        >
          <CardContent>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
              Upload Documents: {collection.replace(/_/g, " ")}
            </Typography>

            {!isSupportedCollection() && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: "#fff3cd",
                  border: "1px solid #ffc107",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <WarningIcon sx={{ color: "#ff9800" }} />
                <Typography sx={{ color: "#856404" }}>
                  This collection is not yet configured for document upload.
                  Supported collections:{" "}
                  {DOMAIN_COLLECTIONS.concat(ARCHETYPE_COLLECTIONS)
                    .concat(CLIENT_COLLECTIONS)
                    .join(", ")}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                border: "2.5px dashed #BDBDBD",
                borderRadius: 3,
                p: 5,
                mb: 3,
                textAlign: "center",
                background: "#FAFAFA",
                cursor:
                  statusBarActive || !isSupportedCollection()
                    ? "not-allowed"
                    : "pointer",
                transition: "border-color 0.2s",
                "&:hover": {
                  borderColor:
                    statusBarActive || !isSupportedCollection()
                      ? "#BDBDBD"
                      : "#1976d2",
                },
                opacity: !isSupportedCollection() ? 0.6 : 1,
              }}
              onClick={() => {
                if (!statusBarActive && isSupportedCollection())
                  handleAreaClick();
              }}
            >
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                multiple
                onChange={handleFileChange}
                disabled={statusBarActive || !isSupportedCollection()}
              />

              <CloudUploadIcon sx={{ fontSize: 60, color: "#90caf9", mb: 2 }} />
              <Typography
                sx={{ color: "#757575", fontSize: 20, fontWeight: 500 }}
              >
                {selectedFiles.length > 0
                  ? `${selectedFiles.length} file(s) selected`
                  : "Click or drag files to this area to upload"}
              </Typography>
            </Box>

            <Typography sx={{ color: "#757575", fontSize: 16, mb: 3 }}>
              Formats accepted are <b>.pdf, .doc, .txt, .docx</b>
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", mt: 3 }}>
              {domainScore !== null ? (
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 500,
                    color:
                      uploadResult === "Upload successful!"
                        ? "#222"
                        : "error.main",
                    flex: 1,
                  }}
                >
                  <b>Domain Detection Score:</b>{" "}
                  {(domainScore * 100).toFixed(1)}%
                </Typography>
              ) : (
                <span style={{ flex: 1 }} />
              )}
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setSelectedFiles([])} // ✅ reset to empty array, not null
                  disabled={selectedFiles.length === 0 || uploading}
                  sx={{ fontSize: 16, px: 3, py: 1.5, borderRadius: 2 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleContinue}
                  disabled={
                    (selectedFiles?.length || 0) === 0 ||
                    uploading ||
                    uploadResult === "Upload successful!" ||
                    statusBarActive ||
                    !isSupportedCollection()
                  }
                  sx={{ fontSize: 16, px: 4, py: 1.5, borderRadius: 2 }}
                >
                  {uploading ? "Uploading..." : "Continue"}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={handleCancelUpload}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <InfoIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Document Analysis Complete
          </Typography>
        </DialogTitle>
        <DialogContent>
          {previewResults.length > 0 ? (
            previewResults.map((res, idx) => {
              const det = res.detection || {};
              const score = det.score || 0;
              return (
                <Box
                  key={idx}
                  sx={{
                    mb: 3,
                    p: 2,
                    bgcolor: "#f8f9fa",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ mb: 0.5 }}
                  >
                    {res.file}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Predicted Domain: <b>{det.predicted_domain || "—"}</b>
                  </Typography>

                  <Chip
                    label={`${(score * 100).toFixed(1)}% Match`}
                    color={
                      score >= 0.3
                        ? "success"
                        : score >= 0.2
                        ? "warning"
                        : "error"
                    }
                    variant="outlined"
                    sx={{ fontWeight: 600, mb: 1 }}
                  />

                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {det.accept ? (
                      <>
                        <CheckCircleIcon color="success" fontSize="small" />
                        <Typography variant="body2" color="success.main">
                          Accepted for Upload
                        </Typography>
                      </>
                    ) : (
                      <>
                        <WarningIcon color="error" fontSize="small" />
                        <Typography variant="body2" color="error.main">
                          Rejected (Low Match)
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
              );
            })
          ) : (
            <Typography>No analysis data available.</Typography>
          )}

          <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 2 }}>
            <Typography variant="body1" fontWeight={500} gutterBottom>
              Would you like to proceed with uploading these document(s)?
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCancelUpload}
            variant="outlined"
            color="inherit"
            sx={{ px: 3, py: 1.5, borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUpload}
            variant="contained"
            color="primary"
            sx={{ px: 3, py: 1.5, borderRadius: 2 }}
          >
            Upload Document
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default KnowledgeBaseUpload;
